import { useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Calculator, Warning, CheckCircle, Info } from "@phosphor-icons/react";
import { Field } from "./shared";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const TILES = [
  { key: "ohio_rd_increase", label: "R&D increase over 3-yr avg" },
  { key: "ohio_rd_credit", label: "Ohio R&D ITC (7%)", color: "#C77DFF" },
  { key: "jctc_est_withholdings", label: "Est. OH withholdings" },
  { key: "jctc_annual_benefit", label: "JCTC annual benefit", color: "#5B8DEF" },
  { key: "total_ohio_benefit", label: "Total Ohio benefit", color: "#00A86B" },
];

export default function UsOhioCalc({ onSaved }) {
  const [f, setF] = useState({ ohio_current_qre: "", op1: "", op2: "", op3: "", jctc_new_payroll: "", jctc_percentage: "", tax_year: new Date().getFullYear() });
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const priors = [f.op1, f.op2, f.op3].map((x) => Number(x || 0)).filter((x) => x > 0);
      const r = await api.post("/tax/calculate/us-ohio-credits", {
        ohio_current_qre: Number(f.ohio_current_qre || 0),
        ohio_prior_qres: priors,
        jctc_new_payroll: Number(f.jctc_new_payroll || 0),
        jctc_percentage: Number(f.jctc_percentage || 0),
        tax_year: Number(f.tax_year),
      });
      setCalc(r.data);
      onSaved && onSaved();
    } catch (err) { notifyError(err); }
    finally { setBusy(false); }
  };

  const r = calc?.results;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={run} className="glass rounded-2xl p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 eyebrow mb-1">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#C77DFF22", color: "#C77DFF", border: "1px solid #C77DFF" }}>OHIO</span>
            State incentives
          </div>
          <p className="text-white/55 text-sm">Ohio R&D Investment Tax Credit (7% of the increase in Ohio QREs, against the CAT) and the Job Creation Tax Credit (refundable, on new-job withholdings).</p>
        </div>
        <div className="eyebrow text-[10px]">Ohio R&D Investment Tax Credit</div>
        <Field label="Current-year Ohio QREs ($)" type="number" step="0.01" data-testid="ohio-current" value={f.ohio_current_qre} onChange={(e) => set("ohio_current_qre", e.target.value)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Prior yr 1 ($)" type="number" step="0.01" data-testid="ohio-p1" value={f.op1} onChange={(e) => set("op1", e.target.value)} />
          <Field label="Prior yr 2 ($)" type="number" step="0.01" data-testid="ohio-p2" value={f.op2} onChange={(e) => set("op2", e.target.value)} />
          <Field label="Prior yr 3 ($)" type="number" step="0.01" data-testid="ohio-p3" value={f.op3} onChange={(e) => set("op3", e.target.value)} />
        </div>
        <div className="eyebrow text-[10px] pt-2">Job Creation Tax Credit</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New-job annual payroll ($)" type="number" step="0.01" data-testid="ohio-payroll" value={f.jctc_new_payroll} onChange={(e) => set("jctc_new_payroll", e.target.value)} />
          <Field label="Awarded JCTC %" type="number" step="0.01" data-testid="ohio-jctc-pct" value={f.jctc_percentage} onChange={(e) => set("jctc_percentage", e.target.value)} />
        </div>
        <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="ohio-calculate"><Calculator size={16} weight="fill" /> {busy ? "Calculating…" : "Estimate Ohio benefit"}</button>
      </form>

      {r ? (
        <div className="glass rounded-2xl p-6 space-y-5" data-testid="ohio-result">
          <div className="eyebrow">Estimated Ohio incentives</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TILES.map((t) => (
              <div key={t.key} className="rounded-xl bg-black/30 border border-white/5 p-4" data-testid={`ohio-tile-${t.key}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{t.label}</div>
                <div className="mono text-2xl mt-2" style={t.color ? { color: t.color } : undefined}>{usd(r[t.key])}</div>
              </div>
            ))}
          </div>
          {r.warnings?.length > 0 && (
            <div className="space-y-2">
              {r.warnings.map((w) => (
                <div key={w} className="flex items-start gap-2 text-sm rounded-lg px-3 py-2" style={{ background: "#FFB02014", border: "1px solid #FFB02044" }} data-testid="ohio-warning">
                  <Warning size={16} weight="fill" className="text-[#FFB020] mt-0.5 shrink-0" /><span className="text-white/80">{w}</span>
                </div>
              ))}
            </div>
          )}
          <div>
            <div className="eyebrow mb-2">Next steps</div>
            <ul className="space-y-1.5">
              {calc.next_steps.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-white/75"><CheckCircle size={16} weight="fill" className="text-[#00A86B] mt-0.5 shrink-0" /> {s}</li>
              ))}
            </ul>
          </div>
          <div className="flex items-start gap-2 text-xs text-white/50 border-t border-white/5 pt-4"><Info size={14} className="mt-0.5 shrink-0" /> {calc.disclaimer}</div>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 flex items-center justify-center text-white/40 text-sm text-center">Enter your Ohio QREs and new-job payroll to estimate state incentives.</div>
      )}
    </div>
  );
}
